import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { T, SIDEBAR_WIDTH } from "./tokens";
import { StatusDot } from "./ui";

export type TandemView =
    | "overview"
    | "schedule"
    | "reports"
    | "pool"
    | "consultants"
    | "settings"
    | "points"
    | "engine";

type NavEntry = { header: string } | { key: TandemView; label: string };

const NAV: NavEntry[] = [
    { header: "Monitor" },
    { key: "overview", label: "Overview" },
    { key: "schedule", label: "Schedule" },
    { key: "reports", label: "Reports" },
    { key: "pool", label: "Visit pool" },
    { key: "consultants", label: "Consultants" },
    { header: "Configure" },
    { key: "settings", label: "Settings" },
    { key: "points", label: "Points model" },
    { key: "engine", label: "Engine settings" },
];

const Sidebar = ({
    active,
    onNavigate,
    hiddenPages,
}: {
    active: TandemView;
    onNavigate: (v: TandemView) => void;
    hiddenPages?: Set<TandemView>;
}) => {
    const navigate = useNavigate();

    // Drop hidden pages, and drop a group header when its whole group is hidden.
    const visibleNav: NavEntry[] = [];
    let pendingHeader: NavEntry | null = null;
    NAV.forEach((entry) => {
        if ("header" in entry) {
            pendingHeader = entry;
            return;
        }
        if (hiddenPages?.has(entry.key)) return;
        if (pendingHeader) {
            visibleNav.push(pendingHeader);
            pendingHeader = null;
        }
        visibleNav.push(entry);
    });

    return (
        <Box
            sx={{
                width: `${SIDEBAR_WIDTH}px`,
                flexShrink: 0,
                background: T.ink,
                color: T.sidebarText,
                display: "flex",
                flexDirection: "column",
                padding: "20px 14px",
            }}
        >
            {/* Brand */}
            <Box
                onClick={() => onNavigate("overview")}
                sx={{ padding: "4px 10px 22px 10px", cursor: "pointer" }}
            >
                <Typography sx={{ fontWeight: 800, fontSize: "19px", letterSpacing: "-0.01em", color: "#fff" }}>
                    Tandem
                </Typography>
                <Typography
                    sx={{ fontSize: "11px", color: T.sidebarMuted, mt: "2px", letterSpacing: "0.06em", textTransform: "uppercase" }}
                >
                    WorkNest Tools · H&amp;S Visits
                </Typography>
            </Box>

            {/* Nav */}
            <Box component="nav" sx={{ display: "flex", flexDirection: "column", gap: "4px", minHeight: 0, overflowY: "auto" }}>
                {visibleNav.map((entry, i) => {
                    if ("header" in entry) {
                        return (
                            <Typography
                                key={`h-${i}`}
                                sx={{
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    color: T.sidebarMuted,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.09em",
                                    padding: "12px 10px 2px 10px",
                                }}
                            >
                                {entry.header}
                            </Typography>
                        );
                    }
                    const isActive = entry.key === active;
                    return (
                        <Box
                            key={entry.key}
                            onClick={() => onNavigate(entry.key)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "8px 10px",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "13.5px",
                                fontWeight: isActive ? 700 : 500,
                                color: isActive ? "#fff" : T.sidebarText,
                                background: isActive ? T.sidebarHover : "transparent",
                                "&:hover": { background: T.sidebarHover },
                            }}
                        >
                            <StatusDot color={isActive ? T.purple : T.sidebarMuted} size={6} />
                            <Box sx={{ flexGrow: 1 }}>{entry.label}</Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Back to the rest of WorkNest Tools */}
            <Box
                onClick={() => navigate("/")}
                sx={{
                    marginTop: "auto",
                    fontSize: "12px",
                    color: T.sidebarMuted,
                    cursor: "pointer",
                    padding: "4px 10px",
                    "&:hover": { color: "#fff" },
                }}
            >
                ← WorkNest Tools
            </Box>
        </Box>
    );
};

export default Sidebar;
